// Thin bridge between apiClient's tracedFetch and the telemetry recorder.
// Lives in its own module so apiClient's static import never touches
// recorder state at module-init time, and so the skip-list (telemetry's own
// endpoints) has exactly one home.

import { AppState } from 'react-native';
import { recorder } from './recorder';

const SKIP_SUBSTRINGS = ['/telemetry/', '/monitoring/client-errors'];

// ── Suspension guard ──────────────────────────────────────────────────────
//
// apiClient times requests with `Date.now() - startedAt` — client wall clock.
// When the OS suspends a mobile app mid-request the JS runtime freezes; on
// resume the promise settles and the elapsed time includes however long the
// phone was asleep. The API was never slow.
//
// This is not theoretical. GET /wellness/health/settings showed a p95 of
// 300.5s over 500 calls at the top of the admin bottlenecks panel — the
// slowest thing in the system by two orders of magnitude — for a handler that
// is one DynamoDB GetItem by primary key. It ranked there because
// runHealthSync calls it on foreground transitions and from the background
// task, which are exactly the moments an app gets suspended. A healthy
// endpoint sat at the top of the "where is it slow" table, which is where
// someone goes to optimise the wrong thing.
//
// So: only record a sample the app was awake for, end to end. A dropped
// sample is a small loss; a fabricated 300-second one is worse than none.
//
// Subscribed lazily rather than at module init — this module exists so that
// apiClient's static import never touches recorder state at import time, and
// an AppState listener at init would break that.
let lastNonActiveAtMs = 0;
let appStateSubscribed = false;

function ensureAppStateWatch(): void {
  if (appStateSubscribed) return;
  appStateSubscribed = true;
  try {
    AppState.addEventListener('change', (state) => {
      if (state !== 'active') lastNonActiveAtMs = Date.now();
    });
  } catch {
    // No AppState (some web/test runtimes) — the guard below then relies on
    // currentState alone, which is the conservative direction.
  }
}

/** True when the app was continuously foregrounded for the whole request. */
function awakeThroughout(startedAt: number, endedAt: number): boolean {
  try {
    // Not foregrounded at completion: either a background-task request or one
    // the app never came back from. Either way the clock is not trustworthy.
    if (AppState.currentState !== 'active') return false;
    // Went away and returned at some point inside the request window.
    return !(lastNonActiveAtMs >= startedAt && lastNonActiveAtMs <= endedAt);
  } catch {
    return true;
  }
}

export function traceApiCall(url: string, method: string, status: number, ms: number): void {
  try {
    if (!url || SKIP_SUBSTRINGS.some((s) => url.includes(s))) return;
    ensureAppStateWatch();
    const endedAt = Date.now();
    if (!awakeThroughout(endedAt - ms, endedAt)) return;
    recorder.recordApi(url, method, status, ms);
  } catch {
    // tracing must never affect the request path
  }
}

// Streaming (XHR/NDJSON) calls bypass apiClient's tracedFetch, so the five
// stream* modules hold one of these handles per request. `firstByte()` is
// called from the first onprogress (time-to-first-token — the number that IS
// perceived chat latency); `done(status)` from onload/onerror/ontimeout.
// Deliberately NOT called on user-initiated abort — a cancelled stream is not
// a latency sample. Both methods are idempotent and swallow everything.
export interface StreamTraceHandle {
  firstByte: () => void;
  done: (status: number) => void;
}

export function traceStreamCall(kind: string): StreamTraceHandle {
  const startedAt = Date.now();
  let ttfbMs: number | null = null;
  let finished = false;
  return {
    firstByte: () => {
      if (ttfbMs === null) ttfbMs = Date.now() - startedAt;
    },
    done: (status: number) => {
      if (finished) return;
      finished = true;
      try {
        recorder.recordStream(kind, status, Date.now() - startedAt, ttfbMs);
      } catch {
        // tracing must never affect the stream
      }
    },
  };
}
